import React from "react";

import { arrayToIndexedObject } from "@/utils/arrayUtil";
import Badge from "@/components/UI/Badge";
import { BehavioralGoalsRating } from "@/features/notes/components/BehavioralGoalsRating";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { createClassNameString } from "@/component-library";
import images from "@/utils/images";
import { RATING_OPTION_LABEL_MAP, SCREENING_OPTION_LABEL_MAP } from "@/constants/rating";

import LocalVideo from "../LocalVideo";
import RemoteVideo from "../RemoteVideo";

import { CarePlanWidget } from "./CarePlanWidget/CarePlanWidget";
import { PatientInfoWidget } from "./PatientInfoWidget";
import { WorkflowStepTransitionButtons } from "./WorkflowStepTransitionButtons";

export const DisplayWorkflowStepWithVideoClient = (componentProps) => {
  const { endCall, jumpWorkflowStep, props, state, step } = componentProps;
  const { currentStep, patientDetails, patientDiagnosis, progressNote } = state;
  let [fullScreenMode, setFullScreenMode] = React.useState(
    state.fullScreen || step.fullScreen || window.innerWidth < 1200,
  );

  React.useEffect(() => {
    function handleResize() {
      setFullScreenMode(state.fullScreen || step.fullScreen || window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleResize);

    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  let videoGroup = (
    <>
      <RemoteVideo
        {...props}
        endCall={endCall}
        fullscreenMode={fullScreenMode}
        note={progressNote}
      />
      <LocalVideo {...props} />
    </>
  );
  let content;

  const shouldAdjustGuidedSessionHeight =
    currentStep && state?.workflow?.steps?.[currentStep]?.isDisplayingGuidedSession;

  if (fullScreenMode) {
    content = (
      <>
        <div className="GuidedVideoCall__whole_screen_view">{videoGroup}</div>
      </>
    );
  } else {
    content = (
      <div
        className={createClassNameString([
          "GuidedVideoCall__guided_step row",
          shouldAdjustGuidedSessionHeight ? "GuidedVideoCall__adjusted_height" : null,
        ])}
      >
        <div className="GuidedVideoCall__video_details col-12 col-md-12 col-lg-7">
          <div className="d-none d-md-none d-lg-block">
            <PatientInfoWidget
              isLarger={false}
              patientDetails={patientDetails}
              patientDiagnosis={patientDiagnosis}
            />
          </div>

          <div className="GuidedVideoCall__guided_step_view">{videoGroup}</div>

          <CarePlanWidget
            carePlanTips={step.carePlanTips}
            noteContent={progressNote?.content}
            patientDetails={patientDetails}
          />
        </div>

        <div className="GuidedVideoCall__guided_widget px-0 d-none d-md-none d-lg-block col-lg-5">
          <CardSecondary className="h-100 Background">
            <div className="GuidedVideoCall__guided_widget_header_container">
              <div className="GuidedVideoCall__guided_widget_header_title">{step["caption"]}</div>
              <div className="GuidedVideoCall__guided_widget_header_subtitle">{step?.subTitle}</div>
            </div>

            <div className="GuidedVideoCall__guided_widget_content">
              {step.subComponent && state.refresh && <step.subComponent {...componentProps} />}
            </div>

            {!step.customTransition && (
              <WorkflowStepTransitionButtons
                currentStep={currentStep}
                onNextStep={() => jumpWorkflowStep(currentStep + 1)}
                onPreviousStep={() => jumpWorkflowStep(currentStep - 1)}
                workflow={state.workflow}
              />
            )}
          </CardSecondary>
        </div>
      </div>
    );
  }

  return <>{content}</>;
};

export const ExpandingTextArea = (props) => {
  let [expand, setExpand] = React.useState(false);

  return (
    <CardSecondary className="p-3 m-3" style={{ height: expand ? "95%" : "25%" }}>
      <p className="tw:font-bold">{props.label}</p>
      <textarea
        className="w-100"
        onChange={props.onChange}
        style={{ height: !expand ? "60%" : "90%", maxHeight: "90%", resize: "none" }}
        value={props.value}
      />
      <div>
        <Badge
          classes="m-auto"
          onClick={() => setExpand(!expand)}
          text={!expand ? "Expand" : "Collapse"}
          type="light-grey"
        />
      </div>
    </CardSecondary>
  );
};

export const ExpandingSelect = (props) => {
  let [selecting, setSelecting] = React.useState(false);

  let controls = (
    <>
      <div className={`float-left ${props.labelClass}`}>{props.label}</div>
      <div className="float-right" onClick={() => setSelecting(!selecting)}>
        <img
          src={images("./common/chevron.svg")}
          style={{
            height: 15,
            transform: selecting ? "rotate(180deg)" : "rotate(0)",
            width: 15,
          }}
        />
      </div>
    </>
  );

  let answer = Object.entries(props.options)
    .filter(([_optionKey, optionValue]) => optionValue.answer)
    .map(([_optionKey, optionValue]) => optionValue.label)
    .join(", ");

  answer = answer ? answer : "None";

  let displayContent = (
    <CardSecondary className={"px-2 py-3 " + props.className} style={props.style}>
      <div>{controls}</div>
      <br />
      <div>{answer}</div>
    </CardSecondary>
  );

  let selectingContent = (
    <CardSecondary className={"px-2 py-3 position-sticky " + props.className} style={props.style}>
      <div>{controls}</div>
      <br />
      {Object.entries(props.options)
        .sort((a, b) => a[1].order - b[1].order)
        .map(([optionKey, optionValue]) => {
          return (
            <div key={`${optionValue.label}${optionValue.order}`}>
              <label className="position-absolute">
                <input
                  checked={optionValue.answer ? optionValue.answer : null}
                  className="custom-checkbox mr-2"
                  disabled={props.disabled ? "disabled" : null}
                  onChange={(e) => props.onChange(e, optionKey, "answer")}
                  type="checkbox"
                  value={{ optionKey, optionValue }}
                />
                {optionValue.label}
              </label>
              <br />
            </div>
          );
        })}
    </CardSecondary>
  );

  return selecting ? selectingContent : displayContent;
};

export const IndividualSymptomTrend = (props) => {
  const { answer } = props;
  let labelOptions;

  if (answer.rating_labels) {
    labelOptions = arrayToIndexedObject(answer.rating_labels);
  } else {
    labelOptions =
      answer.type == null || answer.type === "rating"
        ? RATING_OPTION_LABEL_MAP
        : SCREENING_OPTION_LABEL_MAP;
  }

  return (
    <CardSecondary className="m-3 p-3 grey-container">
      <p className="fs-12 tw:font-bold">{answer.label}</p>
      <div className="d-flex justify-content-start">
        {Object.values(labelOptions).map((optionLabel, index) => (
          <div className="w-25 fs-10 text-center tw:font-bold" key={optionLabel + index}>
            <div className="m-auto">{optionLabel}</div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-start">
        <BehavioralGoalsRating
          containerClass="p-0"
          isEditing={false}
          optionLabelMap={labelOptions}
          progress={answer}
        />
      </div>
    </CardSecondary>
  );
};
